import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatINR, BACKEND_URL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, QrCode, ShieldCheck, CreditCard, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { validateShippingForm, normalizeShippingForm } from "@/lib/validation";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loginWithGoogle } = useAuth();
  const [step, setStep] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [method, setMethod] = useState("razorpay");
  const [rzpConfig, setRzpConfig] = useState({ enabled: false });
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, amount_off, discount}
  const [couponBusy, setCouponBusy] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    state: "Gujarat",
    postal_code: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Live-validate on any form change so already-touched fields update their error state
  useEffect(() => {
    const { errors: e } = validateShippingForm(form);
    setErrors(e);
  }, [form]);

  const shipping =
    subtotal === 0 ? 0 : subtotal >= 499 ? 0 : (form.state || "").trim().toLowerCase() === "gujarat" ? 49 : 99;
  const couponDiscount = appliedCoupon ? Math.min(appliedCoupon.amount_off, subtotal) : 0;
  const total = Math.max(0, subtotal + shipping - couponDiscount);

  useEffect(() => {
    api.get("/payments/razorpay/config").then((r) => {
      setRzpConfig(r.data);
      if (!r.data.enabled) setMethod("upi_qr");
    });
    loadRazorpayScript();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setAppliedCoupon({ code: data.code, amount_off: data.amount_off, discount: data.discount });
      toast.success(`Coupon ${data.code} applied! ₹${data.discount} off`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const placeUpiOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_method: "upi_qr",
        coupon_code: appliedCoupon?.code || null,
      });
      clear();
      navigate(`/order-success?id=${data.order_id}`);
    } catch (e) {
      toast.error("Could not place order. Check details.");
    } finally {
      setPlacing(false);
    }
  };

  const payWithRazorpay = async () => {
    setPlacing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load payment gateway. Check your connection.");
        return;
      }
      const { data } = await api.post("/payments/razorpay/create-order", {
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        coupon_code: appliedCoupon?.code || null,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Blisson Professional",
        description: `Order ${data.blisson_order_id}`,
        image: "https://customer-assets.emergentagent.com/job_blisson-elegance/artifacts/4l9vaw03_Create_a_premium_luxury_cosmetic_202605221730.jpeg",
        prefill: {
          name: data.name,
          email: data.email,
          contact: data.phone,
        },
        notes: { blisson_order_id: data.blisson_order_id },
        theme: { color: "#000000" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: data.blisson_order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            navigate(`/order-success?id=${data.blisson_order_id}`);
          } catch (e) {
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPlacing(false);
            toast.message("Payment cancelled. Your order is on hold.");
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setPlacing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start payment. Try again.");
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-40 text-center min-h-[70vh]" data-testid="checkout-empty">
        <div className="font-serif-display text-4xl mb-4">Your bag is empty.</div>
        <Link to="/shop" className="btn-outline mt-4 inline-flex" data-testid="checkout-empty-shop">Discover the Collection</Link>
      </div>
    );
  }

  const upiId = "yespay.bizsbiz127743@yesbankltd";
  const payeeName = "ELEGANCE EMPIRE LLP";
  const qrImg = "https://customer-assets.emergentagent.com/job_blisson-elegance/artifacts/xgazdsot_WhatsApp%20Image%202026-05-23%20at%203.44.04%20PM.jpeg";

  return (
    <div className="pt-32 pb-20 bg-white min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <div className="overline mb-3">Checkout</div>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter font-light">
            Complete your <em className="gold-text not-italic">ritual</em>
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            {!user && (
              <div className="border border-[#C9A961]/40 bg-[#F8F5F0] p-6 mb-8" data-testid="checkout-login-banner">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-serif-display text-xl">Sign in for a faster experience</div>
                    <div className="text-sm text-[#5C5C5C] mt-1">Save your address, track orders and unlock loyalty perks.</div>
                  </div>
                  <button onClick={loginWithGoogle} className="btn-outline" data-testid="checkout-google-login">Continue with Google</button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-8" data-testid="checkout-steps">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${step >= s ? "bg-black text-white" : "bg-[#F8F5F0] text-[#5C5C5C]"}`}>{s}</div>
                  <div className="overline">{s === 1 ? "Shipping" : "Payment"}</div>
                  {s === 1 && <div className="w-16 h-px bg-black/10" />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="checkout-shipping-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Full Name"
                    value={form.full_name}
                    onChange={(v) => update("full_name", v)}
                    onBlur={() => setTouched((t) => ({ ...t, full_name: true }))}
                    error={touched.full_name && errors.full_name}
                    testid="ship-name"
                    autoComplete="name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    error={touched.email && errors.email}
                    testid="ship-email"
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
                <Field
                  label="Phone (10-digit Indian mobile)"
                  value={form.phone}
                  onChange={(v) => update("phone", v)}
                  onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                  error={touched.phone && errors.phone}
                  testid="ship-phone"
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={15}
                />
                <Field
                  label="Address"
                  value={form.address}
                  onChange={(v) => update("address", v)}
                  onBlur={() => setTouched((t) => ({ ...t, address: true }))}
                  error={touched.address && errors.address}
                  testid="ship-address"
                  autoComplete="street-address"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="City"
                    value={form.city}
                    onChange={(v) => update("city", v)}
                    onBlur={() => setTouched((t) => ({ ...t, city: true }))}
                    error={touched.city && errors.city}
                    testid="ship-city"
                    autoComplete="address-level2"
                  />
                  <StateField value={form.state} onChange={(v) => update("state", v)} />
                </div>
                <Field
                  label="Postal Code (6-digit PIN)"
                  value={form.postal_code}
                  onChange={(v) => update("postal_code", v.replace(/\D/g, "").slice(0, 6))}
                  onBlur={() => setTouched((t) => ({ ...t, postal_code: true }))}
                  error={touched.postal_code && errors.postal_code}
                  testid="ship-postal"
                  autoComplete="postal-code"
                  inputMode="numeric"
                  maxLength={6}
                />
                <Field label="Order Notes (optional)" value={form.notes} onChange={(v) => update("notes", v)} testid="ship-notes" />
                <button
                  onClick={() => {
                    const { errors: e, hasErrors } = validateShippingForm(form);
                    // Mark every required field as touched so all errors surface at once
                    setTouched({
                      full_name: true, email: true, phone: true, address: true,
                      city: true, state: true, postal_code: true,
                    });
                    setErrors(e);
                    if (hasErrors) {
                      const firstKey = ["full_name","email","phone","address","city","state","postal_code"].find((k) => e[k]);
                      toast.error(e[firstKey] || "Please fix the highlighted fields.");
                      // Scroll first error into view
                      requestAnimationFrame(() => {
                        const el = document.querySelector(`[data-testid="input-ship-${firstKey === "full_name" ? "name" : firstKey === "postal_code" ? "postal" : firstKey}"]`);
                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                        el?.focus?.();
                      });
                      return;
                    }
                    setForm((prev) => normalizeShippingForm(prev));
                    setStep(2);
                  }}
                  className="btn-primary"
                  data-testid="checkout-continue-payment"
                >
                  Continue to Payment →
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="checkout-payment-section">
                {/* Method selector */}
                <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="payment-method-selector">
                  {rzpConfig.enabled && (
                    <button
                      onClick={() => setMethod("razorpay")}
                      className={`border p-5 text-left transition-colors ${method === "razorpay" ? "border-black bg-black text-white" : "border-black/15 hover:border-black"}`}
                      data-testid="method-razorpay"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <CreditCard size={16} strokeWidth={1.4} />
                        <span className="overline">{method === "razorpay" ? "Selected" : "Recommended"}</span>
                      </div>
                      <div className="font-serif-display text-xl">Pay Online</div>
                      <div className={`text-xs mt-1 ${method === "razorpay" ? "text-white/70" : "text-[#5C5C5C]"}`}>
                        Card · UPI · NetBanking · Wallets · Instant
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => setMethod("upi_qr")}
                    className={`border p-5 text-left transition-colors ${method === "upi_qr" ? "border-black bg-black text-white" : "border-black/15 hover:border-black"}`}
                    data-testid="method-upi-qr"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <QrCode size={16} strokeWidth={1.4} />
                      <span className="overline">{method === "upi_qr" ? "Selected" : "Manual"}</span>
                    </div>
                    <div className="font-serif-display text-xl">Scan UPI QR</div>
                    <div className={`text-xs mt-1 ${method === "upi_qr" ? "text-white/70" : "text-[#5C5C5C]"}`}>
                      Static QR · We verify manually (30 min)
                    </div>
                  </button>
                </div>

                {method === "razorpay" && rzpConfig.enabled && (
                  <div className="border border-[#C9A961]/30 p-7 bg-[#F8F5F0]/40" data-testid="razorpay-section">
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard size={22} className="text-[#C9A961]" strokeWidth={1.4} />
                      <div>
                        <div className="font-serif-display text-2xl">Secure Online Payment</div>
                        <div className="text-xs text-[#5C5C5C] mt-1">Powered by Razorpay · 256-bit SSL · PCI-DSS Compliant</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 my-5">
                      <div className="flex items-center justify-between border-b border-black/5 pb-2 text-sm">
                        <span className="overline">Amount</span>
                        <span className="font-medium font-serif-display text-xl">{formatINR(total)}</span>
                      </div>
                      <div className="flex items-center justify-between border-b border-black/5 pb-2 text-sm">
                        <span className="overline">Mode</span>
                        <span className="text-xs uppercase tracking-widest">{rzpConfig.mode === "live" ? "Live" : "Test"}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-[#5C5C5C] leading-relaxed">
                      <ShieldCheck size={14} className="text-[#C9A961] mt-0.5 flex-shrink-0" />
                      <span>Click below — Razorpay's secure window opens with all payment options (Card, UPI, NetBanking, Wallets). You'll return here after success.</span>
                    </div>
                  </div>
                )}

                {method === "upi_qr" && (
                  <div className="border border-[#C9A961]/30 p-7 bg-[#F8F5F0]/40" data-testid="upi-qr-section">
                    <div className="flex items-center gap-3 mb-5">
                      <QrCode size={22} className="text-[#C9A961]" strokeWidth={1.4} />
                      <div>
                        <div className="font-serif-display text-2xl">Scan to Pay · UPI QR</div>
                        <div className="text-xs text-[#5C5C5C] mt-1">Any UPI app · GPay · PhonePe · Paytm · BHIM</div>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-7 items-center md:items-start">
                      <div className="bg-white p-4 border border-black/5 luxury-shadow flex flex-col items-center">
                        <img src={qrImg} alt="UPI QR — Elegance Empire LLP" className="w-52 h-52 object-contain" data-testid="upi-qr-image" />
                        <div className="text-[10px] tracking-[0.22em] uppercase text-center mt-3 font-medium">{payeeName}</div>
                      </div>
                      <div className="text-sm flex-1 space-y-3 w-full">
                        <div className="flex items-center justify-between border-b border-black/5 pb-2">
                          <span className="overline">Pay to</span>
                          <span className="font-medium">{payeeName}</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-black/5 pb-2 gap-3">
                          <span className="overline flex-shrink-0">UPI ID</span>
                          <span className="font-mono text-xs break-all text-right" data-testid="checkout-upi-id">{upiId}</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-black/5 pb-2">
                          <span className="overline">Amount</span>
                          <span className="font-medium font-serif-display text-xl">{formatINR(total)}</span>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-[#5C5C5C] pt-2 leading-relaxed">
                          <ShieldCheck size={14} className="text-[#C9A961] mt-0.5 flex-shrink-0" />
                          <span>Scan the QR with any UPI app, enter the exact amount above and pay. Then click <strong>Place Order</strong> — we verify and dispatch within 30 minutes.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 mt-8">
                  <button onClick={() => setStep(1)} className="btn-outline" data-testid="checkout-back">← Back</button>
                  {method === "razorpay" ? (
                    <button
                      onClick={payWithRazorpay}
                      disabled={placing}
                      className="btn-primary flex-1 disabled:opacity-60"
                      data-testid="checkout-pay-razorpay"
                    >
                      {placing ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                      {placing ? "Opening…" : `Pay ${formatINR(total)}`}
                    </button>
                  ) : (
                    <button
                      onClick={placeUpiOrder}
                      disabled={placing}
                      className="btn-primary flex-1 disabled:opacity-60"
                      data-testid="checkout-place-order"
                    >
                      {placing ? "Placing…" : "Place Order"}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#F8F5F0] p-7 lg:sticky lg:top-32" data-testid="checkout-summary">
              <div className="overline mb-4">Order Summary</div>
              <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3" data-testid={`summary-item-${i.product_id}`}>
                    <div className="w-16 h-20 bg-white flex-shrink-0">
                      <img src={i.image.startsWith("http") || i.image.startsWith("data:") ? i.image : `${BACKEND_URL}${i.image}`} alt={i.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="font-serif-display text-base leading-tight">{i.name}</div>
                      <div className="text-xs text-[#5C5C5C] mt-1">Qty {i.quantity}</div>
                    </div>
                    <div className="text-sm">{formatINR(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-black/5 mt-5 pt-5 space-y-2 text-sm">
                <div className="flex items-center justify-between"><span className="text-[#5C5C5C]">Subtotal</span><span data-testid="summary-subtotal">{formatINR(subtotal)}</span></div>
                <div className="flex items-center justify-between">
                  <span className="text-[#5C5C5C]">
                    Shipping{shipping > 0 ? ` · ${(form.state || "").trim().toLowerCase() === "gujarat" ? "Gujarat" : "Out of Gujarat"}` : ""}
                  </span>
                  <span data-testid="summary-shipping">{shipping === 0 ? "Complimentary" : formatINR(shipping)}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex items-center justify-between text-[#C9A961]" data-testid="summary-discount">
                    <span className="flex items-center gap-2">
                      Discount · <span className="font-mono uppercase">{appliedCoupon.code}</span>
                      <button onClick={removeCoupon} className="text-[10px] underline text-[#5C5C5C] hover:text-black" data-testid="coupon-remove">remove</button>
                    </span>
                    <span>− {formatINR(couponDiscount)}</span>
                  </div>
                )}
              </div>

              {/* Coupon input */}
              {!appliedCoupon && (
                <div className="mt-4 flex items-stretch gap-2" data-testid="coupon-input-row">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    placeholder="Discount code"
                    className="flex-1 bg-white border border-black/15 focus:border-[#C9A961] outline-none px-3 py-2 text-xs uppercase tracking-wider transition-colors"
                    data-testid="coupon-input"
                    onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={couponBusy || !couponInput.trim()}
                    className="bg-black text-white text-xs uppercase tracking-wider px-4 py-2 disabled:opacity-50 hover:bg-[#C9A961] transition-colors"
                    data-testid="coupon-apply"
                  >
                    {couponBusy ? "…" : "Apply"}
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between pt-5 mt-4 border-t border-black/15">
                <span className="overline">Total</span>
                <span className="font-serif-display text-3xl" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onBlur, error, type = "text", testid, autoComplete, inputMode, maxLength }) {
  const hasError = Boolean(error);
  return (
    <label className="block">
      <div className={`overline mb-2 transition-colors ${hasError ? "text-red-600" : ""}`}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        autoComplete={autoComplete}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={hasError}
        aria-describedby={hasError ? `err-${testid}` : undefined}
        className={`w-full bg-transparent border-b outline-none py-3 text-sm transition-colors ${
          hasError
            ? "border-red-500 focus:border-red-600 text-red-700"
            : "border-black/15 focus:border-[#C9A961]"
        }`}
        data-testid={`input-${testid}`}
      />
      {hasError && (
        <div
          id={`err-${testid}`}
          className="flex items-start gap-1.5 mt-1.5 text-[11px] text-red-600 leading-snug"
          data-testid={`err-${testid}`}
        >
          <AlertCircle size={12} strokeWidth={1.6} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </label>
  );
}

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
  "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

function StateField({ value, onChange }) {
  return (
    <label className="block">
      <div className="overline mb-2">State</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-black/15 focus:border-[#C9A961] outline-none py-3 text-sm transition-colors appearance-none cursor-pointer"
        data-testid="input-ship-state"
      >
        {INDIAN_STATES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    </label>
  );
}
